import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, PageShell } from "@/components/PageShell";

const ProfilePage = () => {
  const { user, updateProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.email) {
      setFormData((prev) => ({ ...prev, email: user.email }));
    }
  }, [user?.email]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password && formData.password !== formData.confirmPassword) {
      toast.error("Konfirmasi password tidak sama");
      return;
    }

    const emailChanged = formData.email !== (user?.email || "");
    const passwordChanged = Boolean(formData.password);
    if (!emailChanged && !passwordChanged) {
      toast.error("Tidak ada perubahan profil");
      return;
    }

    setSaving(true);
    try {
      await updateProfile({
        email: formData.email,
        password: passwordChanged ? formData.password : undefined,
      });

      if (passwordChanged) {
        toast.success("Profil diperbarui. Silakan login ulang.");
        logout();
        navigate("/login", { replace: true });
        return;
      }

      toast.success("Profil berhasil diperbarui");
      setFormData((prev) => ({ ...prev, password: "", confirmPassword: "" }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal memperbarui profil");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell data-testid="profile-page" className="max-w-2xl">
      <PageHeader
        eyebrow="Akun"
        title="Profil Saya"
        description="Perbarui email dan kata sandi akun Anda tanpa mengubah preferensi akses yang sudah berjalan."
      />

      <div className="rounded-lg border bg-card p-6 shadow-sm">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="profile-username">Username</Label>
            <Input
              id="profile-username"
              value={user?.username || ""}
              disabled
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile-email">Email</Label>
            <Input
              id="profile-email"
              data-testid="profile-email-input"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile-password">Password Baru</Label>
            <Input
              id="profile-password"
              data-testid="profile-password-input"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
              placeholder="Kosongkan jika tidak diubah"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile-confirm-password">Konfirmasi Password Baru</Label>
            <Input
              id="profile-confirm-password"
              data-testid="profile-confirm-password-input"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
              placeholder="Ulangi password baru"
            />
          </div>

          <Button data-testid="profile-save-button" type="submit" disabled={saving}>
            {saving ? "Menyimpan..." : "Simpan Perubahan"}
          </Button>
        </form>
      </div>
    </PageShell>
  );
};

export default ProfilePage;
